import { motion } from "framer-motion";
import { Languages, BellRing, Sparkles } from "lucide-react";

const loop = { repeat: Infinity, repeatType: "loop", ease: "easeInOut" };

const PHRASES = [
  "Which topic interests you most?",
  "Quel sujet vous intéresse le plus ?",
  "आपको कौन सी विषय में रुचि है?",
  "¿Qué tema te interesa más?",
];

function PresenterTranslationMock() {
  return (
    <div className="rounded-3xl bg-[#0B0F19] p-8 sm:p-10 relative overflow-hidden" data-testid="showcase-presenter-translation-mock">
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#4F46E5]/20 blur-3xl" />
      <div className="flex items-center justify-between mb-8 relative z-10">
        <span className="text-white/40 text-[11px] font-mono tracking-widest">PRESENTER SCREEN</span>
        <motion.div
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: "#4F46E5" }}
          animate={{ rotate: [0, 12, -12, 0] }}
          transition={{ duration: 2.4, ...loop }}
        >
          <Languages className="w-4 h-4 text-white" />
        </motion.div>
      </div>
      <div className="relative h-20 flex items-center justify-center text-center z-10">
        {PHRASES.map((p, i) => (
          <motion.p
            key={p}
            className="absolute inset-x-0 text-white text-lg sm:text-xl font-bold font-heading px-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{
              duration: 2.6,
              times: [0, 0.12, 0.85, 1],
              delay: i * 2.6,
              repeat: Infinity,
              repeatDelay: (PHRASES.length - 1) * 2.6,
            }}
          >
            {p}
          </motion.p>
        ))}
      </div>
      <div className="flex items-center justify-center gap-2 mt-6 relative z-10">
        {["EN", "FR", "HI", "ES"].map((l, i) => (
          <motion.span
            key={l}
            className="text-[10px] font-bold px-2 py-1 rounded-full border border-white/15 text-white/50"
            animate={{ opacity: [0.4, 1, 0.4], borderColor: ["rgba(255,255,255,0.15)", "#FF5733", "rgba(255,255,255,0.15)"] }}
            transition={{ duration: 2.6, delay: i * 2.6, repeat: Infinity, repeatDelay: (PHRASES.length - 1) * 2.6 }}
          >
            {l}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

function CompletionCelebrationMock() {
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-8 sm:p-10 relative overflow-hidden" data-testid="showcase-completion-bell-mock">
      <div className="flex items-center justify-between mb-8">
        <span className="text-slate-400 text-[11px] font-mono tracking-widest">42 / 42 RESPONDED</span>
        <Sparkles className="w-4 h-4 text-[#FF5733]" />
      </div>
      <div className="relative flex items-center justify-center h-28">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute w-20 h-20 rounded-full border-2"
            style={{ borderColor: "#FF5733" }}
            initial={{ scale: 0.5, opacity: 0.7 }}
            animate={{ scale: 2, opacity: 0 }}
            transition={{ duration: 2, delay: i * 0.6, ...loop }}
          />
        ))}
        {Array.from({ length: 10 }).map((_, i) => (
          <motion.span
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full"
            style={{ background: ["#4F46E5", "#FF5733", "#06B6D4", "#F59E0B", "#10B981"][i % 5] }}
            initial={{ x: 0, y: 0, opacity: 1 }}
            animate={{
              x: Math.cos((i / 10) * 2 * Math.PI) * 70,
              y: Math.sin((i / 10) * 2 * Math.PI) * 70,
              opacity: 0,
            }}
            transition={{ duration: 1.8, delay: 0.4, ...loop, repeatDelay: 1.2 }}
          />
        ))}
        <motion.div
          className="w-16 h-16 rounded-full flex items-center justify-center text-white relative z-10 shadow-lg"
          style={{ background: "#4F46E5" }}
          animate={{ rotate: [0, -16, 16, -10, 10, 0] }}
          transition={{ duration: 1, ...loop, repeatDelay: 1.4 }}
        >
          <BellRing className="w-7 h-7" />
        </motion.div>
      </div>
      <p className="text-center text-sm font-semibold text-slate-500 mt-4">All aboard! Everyone answered.</p>
    </div>
  );
}

export function TranslationCelebrationShowcase() {
  return (
    <section className="px-6 py-20 bg-[#FAFAFC]" data-testid="section-translation-celebration-showcase">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#4F46E5] bg-[#4F46E5]/10 px-3 py-1.5 rounded-full mb-5">
            <Sparkles className="w-3.5 h-3.5" /> Just added
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">The big screen just got smarter</h2>
          <p className="text-slate-600 max-w-xl mx-auto">
            Switch languages and the presenter stage translates too — and the room gets a satisfying bell
            the second everyone's in.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
          <PresenterTranslationMock />
          <CompletionCelebrationMock />
        </div>
      </div>
    </section>
  );
}
